import { useState, useEffect } from "react";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { Capacitor } from "@capacitor/core";

import APP_VERSION from "../appVersion";

const LOCAL_VERSION = APP_VERSION;
const DISMISS_KEY = "onyxlife_update_dismissed";
const CHECK_INTERVAL = 30 * 60 * 1000; // re-check every 30 min

function isNewer(remote: string, local: string): boolean {
  const r = remote.split(".").map(Number);
  const l = local.split(".").map(Number);
  for (let i = 0; i < Math.max(r.length, l.length); i++) {
    const rv = r[i] || 0;
    const lv = l[i] || 0;
    if (rv > lv) return true;
    if (rv < lv) return false;
  }
  return false;
}

export type UpdateInfo = {
  available: boolean;
  latestVersion: string;
  isNative: boolean;
  dismiss: () => void;
};

export default function useUpdateCheck(): UpdateInfo {
  const [latestVersion, setLatestVersion] = useState(LOCAL_VERSION);
  const [dismissed, setDismissed] = useState(() => {
    const stored = localStorage.getItem(DISMISS_KEY);
    return stored || "";
  });

  useEffect(() => {
    const check = async () => {
      try {
        const snap = await getDoc(doc(db, "_system", "appConfig"));
        if (snap.exists()) {
          const data = snap.data();
          // Use platform-specific version if available, otherwise fall back to latestVersion
          const isNative = Capacitor.isNativePlatform();
          const v = (isNative ? data?.latestNativeVersion : data?.latestVersion) || data?.latestVersion;
          if (v) setLatestVersion(v);
        }
      } catch { /* offline or doc missing — skip */ }
    };

    check();
    const interval = setInterval(check, CHECK_INTERVAL);
    return () => clearInterval(interval);
  }, []);

  const available = isNewer(latestVersion, LOCAL_VERSION) && dismissed !== latestVersion;

  return {
    available,
    latestVersion,
    isNative: Capacitor.isNativePlatform(),
    dismiss: () => {
      setDismissed(latestVersion);
      localStorage.setItem(DISMISS_KEY, latestVersion);
    },
  };
}
